import dayjs from "dayjs";
import { create } from "zustand";
import { NotificationExtension } from "../_base/extension/NotificationExtension";
import {
  createTblHome,
  deleteTblHome,
  getTblHomeList,
  TblHomeInput,
  TblHomeRow,
  updateTblHome,
  updateTblHomeStt,
} from "../api/ApiTblHome";
import {
  HomeBlock,
  HomeBlockTemplate,
  SAMPLE_BLOCKS,
  TEMPLATES,
  templateInfo,
} from "../views/HomeBlock/homeBlockData";
import { replaceImageUrls, uploadPendingImages } from "../views/HomeBlock/pendingImages";

type LoadStatus = "idle" | "loading" | "ready" | "error";

// Danh sách block trang chủ lấy từ API TblHome; giữ thay đổi chưa lưu khi chuyển giữa màn danh sách và màn sửa.
type HomeBlockState = {
  blocks: HomeBlock[];
  status: LoadStatus;
  selectedId: string;
  // Block có thay đổi (metadata hoặc config) chưa lưu
  dirtyIds: string[];
  saving: boolean;
  // Tải danh sách một lần; force = tải lại, bỏ thay đổi chưa lưu
  load: (force?: boolean) => Promise<void>;
  select: (id: string) => void;
  updateBlock: (id: string, patch: Partial<HomeBlock>) => void;
  // Đổi metadata ở màn danh sách và tự lưu luôn (không có nút Lưu)
  autoSave: (id: string, patch: Partial<HomeBlock>) => void;
  // Gán config lần đầu mở trình sửa, không tính là thay đổi
  initConfig: (id: string, config: unknown) => void;
  updateConfig: (id: string, updater: (prev: unknown) => unknown) => void;
  // Đổi thứ tự rồi tự gọi update-stt
  move: (id: string, step: number) => void;
  reorder: (fromIndex: number, toIndex: number) => void;
  addBlock: (
    template: HomeBlockTemplate,
    name: string,
    insertIndex: number,
    visible: boolean
  ) => Promise<HomeBlock | null>;
  duplicate: (id: string) => Promise<HomeBlock | null>;
  remove: (id: string) => Promise<boolean>;
  saveBlock: (id: string) => Promise<boolean>;
  // Bỏ thay đổi chưa lưu của một block, lấy lại bản đang lưu trên hệ thống
  revertBlock: (id: string) => Promise<void>;
};

const slug = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const uniqueCode = (blocks: HomeBlock[], base: string) => {
  const used = new Set(blocks.map((b) => b.code));
  let code = base;
  let n = 2;
  while (used.has(code)) {
    code = `${base}-${n}`;
    n++;
  }
  return code;
};

const withId = (ids: string[], id: string) => (ids.includes(id) ? ids : [...ids, id]);

const TEMPLATE_KEYS = new Set<string>(TEMPLATES.map((t) => t.key));

const API_DATE = "YYYY-MM-DDTHH:mm:ss";

const toApiDate = (value: Date | null) => (value ? dayjs(value).format(API_DATE) : "");

const fromApiDate = (value: unknown) => {
  if (typeof value !== "string" || !value) return null;
  const date = dayjs(value);
  return date.isValid() ? date.toDate() : null;
};

const parseConfig = (data: unknown) => {
  if (typeof data !== "string" || !data.trim()) return undefined;
  try {
    return JSON.parse(data) as unknown;
  } catch {
    return undefined;
  }
};

// Một dòng TBL_HOME thành block trên màn. Trường nào API chưa trả thì lấy mặc định.
export const fromTblHomeRow = (row: TblHomeRow): HomeBlock => {
  const code = row.code ?? "";
  const sample = SAMPLE_BLOCKS.find((b) => b.code === code);
  const apiTemplate =
    row.template && TEMPLATE_KEYS.has(row.template) ? (row.template as HomeBlockTemplate) : undefined;
  // get-list có thể chưa trả TEMPLATE: đoán theo mã của 20 block thiết kế, không đoán được thì khoá block
  const template = apiTemplate ?? sample?.template;
  const stt = Number(row.stt);
  return {
    id: String(row.id),
    stt: Number.isFinite(stt) ? stt : undefined,
    templateMissing: !template,
    name: row.title ?? "",
    template: template ?? "banner",
    code,
    thumbId: sample?.thumbId ?? templateInfo(template ?? "banner").thumbId,
    desktop: row.desktop ?? true,
    mobile: row.mobile ?? true,
    visible: row.isVisible ?? true,
    from: fromApiDate(row.from_),
    to: fromApiDate(row.to_),
    config: parseConfig(row.data),
  };
};

// Block thành dữ liệu gửi create / edit (cũng là nội dung tab Dữ liệu lưu)
export const toTblHomeInput = (block: HomeBlock, stt: number): TblHomeInput => ({
  id: block.id,
  stt: String(stt),
  code: block.code,
  title: block.name,
  data: block.config === undefined ? "" : JSON.stringify(block.config),
  isVisible: block.visible,
  template: block.template,
  desktop: block.desktop,
  mobile: block.mobile,
  from_: toApiDate(block.from),
  to_: toApiDate(block.to),
});

// Lấy lại bản trên hệ thống nhưng giữ giá trị đang có ở máy cho trường API chưa trả
const mergeFromRow = (row: TblHomeRow, local: HomeBlock): HomeBlock => {
  const fresh = fromTblHomeRow(row);
  return {
    ...fresh,
    ...(row.template == null && {
      template: local.template,
      templateMissing: local.templateMissing,
      thumbId: local.thumbId,
    }),
    ...(row.desktop == null && { desktop: local.desktop }),
    ...(row.mobile == null && { mobile: local.mobile }),
    ...(row.from_ === undefined && { from: local.from }),
    ...(row.to_ === undefined && { to: local.to }),
  };
};

const NO_STT = Number.MAX_SAFE_INTEGER;

// Chờ người dùng dừng tay rồi mới gọi API, để kéo / bấm / chọn lịch liên tục chỉ lưu một lần
const AUTO_SAVE_MS = 500;

export const useHomeBlockStore = create<HomeBlockState>((set, get) => {
  let orderTimer: ReturnType<typeof setTimeout> | undefined;
  const blockTimers = new Map<string, ReturnType<typeof setTimeout>>();

  // Lưu thứ tự theo vị trí trên màn (STT = vị trí + 1)
  const saveOrder = async () => {
    const items = get().blocks.map((b, i) => ({ id: b.id, stt: i + 1 }));
    if (items.length === 0) return true;
    return updateTblHomeStt(items);
  };

  // Lưu thứ tự lỗi: xếp lại theo STT đang lưu trên hệ thống, giữ các sửa đổi khác trên máy
  const restoreOrder = async () => {
    const rows = await getTblHomeList();
    if (!rows) return;
    const sttById = new Map(rows.map((r) => [String(r.id), Number(r.stt)]));
    const sttOf = (b: HomeBlock) => {
      const stt = sttById.get(b.id);
      return stt !== undefined && Number.isFinite(stt) ? stt : NO_STT;
    };
    set((s) => ({ blocks: [...s.blocks].sort((a, b) => sttOf(a) - sttOf(b)) }));
  };

  const scheduleOrderSave = () => {
    clearTimeout(orderTimer);
    orderTimer = setTimeout(async () => {
      if (await saveOrder()) NotificationExtension.Success("Đã lưu thứ tự block");
      else await restoreOrder();
    }, AUTO_SAVE_MS);
  };

  const saveOne = async (id: string) => {
    const target = get().blocks.find((b) => b.id === id);
    if (!target) return false;
    if (target.templateMissing) {
      NotificationExtension.Fails(
        `API chưa trả mẫu (TEMPLATE) của block ${target.name || target.code}, chưa lưu để tránh ghi sai mẫu.`
      );
      return false;
    }
    // Ảnh mới chọn mới chỉ là URL blob: để xem trước: tải lên CDN xong mới lưu block
    const urls = await uploadPendingImages(target.config);
    if (!urls) return false;
    if (urls.size > 0) {
      set((s) => ({
        blocks: s.blocks.map((b) =>
          b.id === id ? { ...b, config: replaceImageUrls(b.config, urls) } : b
        ),
      }));
    }
    const blocks = get().blocks;
    const index = blocks.findIndex((b) => b.id === id);
    if (index < 0) return false;
    const block = blocks[index];
    // edit ghi đè cả STT: gửi đúng vị trí trên màn, khớp với lần update-stt gần nhất
    const ok = await updateTblHome(toTblHomeInput(block, index + 1));
    // Chỉ bỏ đánh dấu nếu không sửa thêm trong lúc chờ API
    if (ok && get().blocks.find((b) => b.id === id) === block) {
      set((s) => ({ dirtyIds: s.dirtyIds.filter((x) => x !== id) }));
    }
    return ok;
  };

  const scheduleBlockSave = (id: string) => {
    clearTimeout(blockTimers.get(id));
    blockTimers.set(
      id,
      setTimeout(async () => {
        blockTimers.delete(id);
        if (await saveOne(id)) NotificationExtension.Success("Đã lưu thay đổi");
      }, AUTO_SAVE_MS)
    );
  };

  // create không trả id: tạo xong đọc lại danh sách để lấy id theo mã block, chèn vào vị trí rồi lưu thứ tự
  const createAt = async (draft: HomeBlock, insertIndex: number) => {
    set({ saving: true });
    try {
      // Id để API tự sinh
      const { id, ...input } = toTblHomeInput(draft, insertIndex + 1);
      const ok = await createTblHome(input);
      if (!ok) return null;
      const rows = await getTblHomeList();
      const row = rows?.find((r) => r.code === draft.code);
      if (!row) {
        NotificationExtension.Fails("Đã thêm block nhưng chưa đọc lại được từ hệ thống, hãy tải lại trang.");
        return null;
      }
      // Giữ đúng giá trị vừa gửi; API có thể chưa trả lại đủ cột
      const created: HomeBlock = { ...draft, id: String(row.id), stt: insertIndex + 1 };
      set((s) => {
        const blocks = s.blocks.filter((b) => b.id !== created.id);
        blocks.splice(Math.min(Math.max(insertIndex, 0), blocks.length), 0, created);
        return { blocks, selectedId: created.id };
      });
      await saveOrder();
      return created;
    } finally {
      set({ saving: false });
    }
  };

  return {
    blocks: [],
    status: "idle",
    selectedId: "",
    dirtyIds: [],
    saving: false,

    load: async (force = false) => {
      const { status } = get();
      if (!force && (status === "loading" || status === "ready")) return;
      set({ status: "loading" });
      const rows = await getTblHomeList();
      if (rows === null) {
        set({ status: "error" });
        return;
      }
      // API sắp theo ngày tạo; màn hiện theo STT
      const blocks = rows
        .map(fromTblHomeRow)
        .sort((a, b) => (a.stt ?? NO_STT) - (b.stt ?? NO_STT));
      set((s) => ({
        blocks,
        status: "ready",
        dirtyIds: [],
        selectedId: blocks.some((b) => b.id === s.selectedId) ? s.selectedId : blocks[0]?.id ?? "",
      }));
    },

    select: (id) => set({ selectedId: id }),

    updateBlock: (id, patch) =>
      set((state) => ({
        blocks: state.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)),
        dirtyIds: withId(state.dirtyIds, id),
      })),

    autoSave: (id, patch) => {
      get().updateBlock(id, patch);
      scheduleBlockSave(id);
    },

    initConfig: (id, config) =>
      set((state) => ({
        blocks: state.blocks.map((b) =>
          b.id === id && b.config === undefined ? { ...b, config } : b
        ),
      })),

    updateConfig: (id, updater) =>
      set((state) => ({
        blocks: state.blocks.map((b) => (b.id === id ? { ...b, config: updater(b.config) } : b)),
        dirtyIds: withId(state.dirtyIds, id),
      })),

    move: (id, step) => {
      const blocks = [...get().blocks];
      const from = blocks.findIndex((b) => b.id === id);
      const to = from + step;
      if (from < 0 || to < 0 || to >= blocks.length) return;
      const [item] = blocks.splice(from, 1);
      blocks.splice(to, 0, item);
      set({ blocks });
      scheduleOrderSave();
    },

    reorder: (fromIndex, toIndex) => {
      if (fromIndex === toIndex) return;
      const blocks = [...get().blocks];
      const [item] = blocks.splice(fromIndex, 1);
      blocks.splice(toIndex, 0, item);
      set({ blocks });
      scheduleOrderSave();
    },

    addBlock: (template, name, insertIndex, visible) => {
      const info = templateInfo(template);
      const draft: HomeBlock = {
        id: "",
        name: name.trim() || info.defaultName,
        template,
        code: uniqueCode(get().blocks, `home-${slug(name || info.defaultName)}`),
        thumbId: info.thumbId,
        desktop: true,
        mobile: true,
        visible,
        from: null,
        to: null,
      };
      return createAt(draft, insertIndex);
    },

    duplicate: (id) => {
      const blocks = get().blocks;
      const index = blocks.findIndex((b) => b.id === id);
      if (index < 0) return Promise.resolve(null);
      const source = blocks[index];
      const draft: HomeBlock = {
        ...source,
        id: "",
        name: `${source.name} (bản sao)`,
        code: uniqueCode(blocks, `${source.code}-copy`),
        visible: false,
      };
      return createAt(draft, index + 1);
    },

    remove: async (id) => {
      clearTimeout(blockTimers.get(id));
      blockTimers.delete(id);
      set({ saving: true });
      try {
        const ok = await deleteTblHome([id]);
        if (!ok) return false;
        set((state) => {
          const index = state.blocks.findIndex((b) => b.id === id);
          const blocks = state.blocks.filter((b) => b.id !== id);
          const next = blocks[Math.min(Math.max(index, 0), blocks.length - 1)];
          return {
            blocks,
            selectedId: next ? next.id : "",
            dirtyIds: state.dirtyIds.filter((x) => x !== id),
          };
        });
        return true;
      } finally {
        set({ saving: false });
      }
    },

    saveBlock: async (id) => {
      set({ saving: true });
      try {
        return await saveOne(id);
      } finally {
        set({ saving: false });
      }
    },

    revertBlock: async (id) => {
      const rows = await getTblHomeList();
      const row = rows?.find((r) => String(r.id) === id);
      if (!row) return;
      set((state) => ({
        blocks: state.blocks.map((b) => (b.id === id ? mergeFromRow(row, b) : b)),
        dirtyIds: state.dirtyIds.filter((x) => x !== id),
      }));
    },
  };
});
